import React from "react";
import { toast } from "sonner";

import {
  AlertDialog, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/shared/components/ui/alert-dialog";
import { confirmDangerCls, confirmDialogCls } from "@/shared/components/settings-atoms";
import { useDeleteAgentUser, useRestartAgentUser } from "@/shared/api/onboarding";

import type { AgentUser } from "@/shared/types";

export type PersonAction = "restart" | "delete";

const WORDS: Record<PersonAction, { title: string; body: string; button: string; busy: string; done: string }> = {
  restart: {
    title: "Restart onboarding for {name}?",
    body: "Everything they answered and proved is forgotten. Their next message starts the flow again from step 1.",
    button: "Restart",
    busy: "Restarting…",
    done: "Onboarding restarted",
  },
  delete: {
    title: "Delete {name}?",
    body: "Their details, their conversations and the runs that answered them are deleted for good. If they write again, they start as someone new.",
    button: "Delete",
    busy: "Deleting…",
    done: "Deleted",
  },
};

/* Restarting and deleting can't be undone, so they ask first and say what goes. */
export function PersonConfirm({ person, action, onClose }: {
  person: AgentUser;
  action: PersonAction;
  onClose: (done: boolean) => void;
}) {
  const restart = useRestartAgentUser();
  const remove = useDeleteAgentUser();
  const mutation = action === "restart" ? restart : remove;
  const words = WORDS[action];
  const name = person.name ?? "this person";

  return (
    <AlertDialog open onOpenChange={(open) => !open && onClose(false)}>
      <AlertDialogContent className={`${confirmDialogCls} max-w-[420px]`}>
        <AlertDialogHeader>
          <AlertDialogTitle className="text-[15px] tracking-[-0.01em]">{words.title.replace("{name}", name)}</AlertDialogTitle>
          <AlertDialogDescription className="text-[13px] leading-[1.6]">{words.body}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel className="rounded-full text-[13px]">Cancel</AlertDialogCancel>
          <button
            className={confirmDangerCls}
            disabled={mutation.isPending}
            onClick={() =>
              mutation.mutate(person.id, {
                onSuccess: () => {
                  toast.success(words.done);
                  onClose(true);
                },
              })
            }
          >
            {mutation.isPending ? words.busy : words.button}
          </button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
